import React from 'react';
import { Volume2, Mic } from 'lucide-react';
import type { ExampleSentence } from '../types';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';

interface ExampleListProps {
  examples: ExampleSentence[];
  onPractice: (example: ExampleSentence) => void;
}

export function ExampleList({ examples, onPractice }: ExampleListProps) {
  const { speak } = useSpeechSynthesis({ lang: 'en-US' });

  if (examples.length === 0) {
    return null;
  }

  const handlePlay = (text: string, lang: 'en-US' | 'en-GB' = 'en-US') => {
    speak(text.replace(/{\/?it}/g, ''), lang);
  };

  return (
    <div className="w-full max-w-2xl space-y-4">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Example Sentences</h2>
      {examples.map((example, index) => (
        <div
          key={index}
          className="bg-white rounded-xl p-6 shadow-sm border border-purple-100 hover:border-purple-200 transition-all duration-300"
        >
          <div className="flex flex-col md:flex-row items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-lg text-gray-800 leading-relaxed">{example.text.replace(/―/g, '').replace(/{\/?phrase}/g, '').replace(/{\/?it}/g, '')}</p>
              <span className="text-sm text-purple-600 font-medium mt-2 inline-block">
                {example.partOfSpeech}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handlePlay(example.text.replace(/{\/?phrase}/g, '').replace(/{\/?it}/g, ''), 'en-US')}
                className="px-2.5 py-2 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-300 flex items-center gap-1"
                title="Listen to example (US Accent)"
                data-testid={`example-play-us-${index}`}
              >
                <Volume2 className="w-4 h-4" />
                <span>🇺🇸 US</span>
              </button>
              <button
                onClick={() => handlePlay(example.text.replace(/{\/?phrase}/g, '').replace(/{\/?it}/g, ''), 'en-GB')}
                className="px-2.5 py-2 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors duration-300 flex items-center gap-1"
                title="Listen to example (British Accent)"
                data-testid={`example-play-uk-${index}`}
              >
                <Volume2 className="w-4 h-4" />
                <span>🇬🇧 UK</span>
              </button>
              <button
                onClick={() => onPractice(example)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
              >
                <Mic className="w-4 h-4" />
                Practice
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}